import { BASTION, GROUND, LAND, STRUCTURES, type WallClass } from '@somnhold/content';
import { mazeDoorOpen } from './battle/maze.ts';
import { neckOpen } from './battle/neck.ts';
import { DIRS, hexLen, hexToWorld, worldToCell, worldToHex, type Hex } from './hex.ts';
import { fdiv, idiv } from './math/fixed.ts';
import { isInside, isWall } from './walls.ts';
import {
  T_GATE,
  T_INTERIOR,
  T_KEEP,
  T_PATH,
  T_PLAZA,
  T_RAMPART,
  T_FORD,
  T_ROCK,
  T_SPAWN,
  T_VOID,
  T_WOOD,
  type BattleState,
  type LandDef,
  type MapDef,
  type Structure,
} from './types.ts';

/** Cell ids index a (2R+1)² box in axial space: id = (r+R)·W + (q+R). */
export const gridW = (R: number): number => 2 * R + 1;
export const cellId = (R: number, q: number, r: number): number => {
  if (hexLen(q, r) > R) return -1;
  return (r + R) * gridW(R) + (q + R);
};
export const cellQ = (R: number, id: number): number => (id % gridW(R)) - R;
export const cellR = (R: number, id: number): number => idiv(id, gridW(R)) - R;
export const cellHex = (R: number, id: number): Hex => ({ q: cellQ(R, id), r: cellR(R, id) });
export const cellCenter = (R: number, id: number): { x: number; y: number } => hexToWorld(cellQ(R, id), cellR(R, id));

/** Per-radius geometry for the hot loops: each cell's axial coords and its 6 neighbors (DIRS order, −1 off the map). */
export interface Geo {
  q: Int32Array;
  r: Int32Array;
  nb: Int32Array;
}
const GEO = new Map<number, Geo>();
export const geo = (R: number): Geo => {
  let g = GEO.get(R);
  if (!g) {
    const W = gridW(R);
    g = { q: new Int32Array(W * W), r: new Int32Array(W * W), nb: new Int32Array(W * W * 6).fill(-1) };
    for (let id = 0; id < W * W; id++) {
      const q = cellQ(R, id);
      const r = cellR(R, id);
      g.q[id] = q;
      g.r[id] = r;
      if (hexLen(q, r) > R) continue;
      for (let k = 0; k < 6; k++) g.nb[id * 6 + k] = cellId(R, q + (DIRS[k] as Hex).q, r + (DIRS[k] as Hex).r);
    }
    GEO.set(R, g);
  }
  return g;
};

export const buildTerrain = (map: MapDef): number[] => {
  const R = map.radius;
  const W = gridW(R);
  const t: number[] = new Array<number>(W * W).fill(T_VOID);
  if (map.land) return landTerrain(map, map.land, t);
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      const d = hexLen(q, r);
      if (d > R) continue;
      const id = cellId(R, q, r);
      if (map.kind === 'dungeon') continue;
      if (map.kind === 'incursion') {
        if (d === 0 && !map.open) t[id] = T_KEEP;
        else if (d === 0) t[id] = T_INTERIOR;
        else if (d <= map.bastion) t[id] = T_INTERIOR;
        continue;
      }
      if (d === 0) t[id] = T_KEEP;
      else if (d <= BASTION.plazaRadius) t[id] = T_PLAZA;
      else if (isInside(map, q, r)) t[id] = T_INTERIOR;
      else if (isWall(map, q, r)) t[id] = T_RAMPART;
    }
  }
  for (const h of map.dungeon?.floor ?? []) {
    const id = cellId(R, h.q, h.r);
    if (id >= 0) t[id] = T_INTERIOR;
  }
  for (const s of map.sectors) {
    if (map.kind === 'bastion') t[cellId(R, s.gate.q, s.gate.r)] = T_GATE;
    s.path.forEach((h, i) => {
      const id = cellId(R, h.q, h.r);
      if (id >= 0 && t[id] === T_VOID) t[id] = i === 0 ? T_SPAWN : T_PATH;
    });
  }
  for (const h of map.trail ?? []) {
    const id = cellId(R, h.q, h.r);
    if (id >= 0 && t[id] === T_VOID) t[id] = T_PATH;
  }
  return t;
};

/** The bastion from scratch: open ground and ruins are built on, woods and fords only walked, rock and deep water neither. */
const landTerrain = (map: MapDef, land: LandDef, t: number[]): number[] => {
  const R = map.radius;
  for (let r = -R; r <= R; r++) {
    for (let q = -R; q <= R; q++) {
      const d = hexLen(q, r);
      if (d > R) continue;
      const id = cellId(R, q, r);
      const k = land.kind[id] as number;
      if (d === 0) t[id] = T_KEEP;
      else if (d <= BASTION.plazaRadius) t[id] = T_PLAZA;
      else if (k === GROUND.rock || k === GROUND.water || k === GROUND.vein) t[id] = T_ROCK;
      else if (k === GROUND.wood) t[id] = T_WOOD;
      // A trench dug by the group walks like a ford.
      else if (k === GROUND.ford || k === GROUND.ditch) t[id] = T_FORD;
      else t[id] = T_INTERIOR;
    }
  }
  for (const s of map.sectors) {
    const id = cellId(R, (s.path[0] as Hex).q, (s.path[0] as Hex).r);
    if (id >= 0 && t[id] !== T_ROCK) t[id] = T_SPAWN;
  }
  return t;
};

/**
 * Stepping on foot from cell `a` into its neighbour `b` of terraced land: −1 where it can't be done (a drop
 * of two levels, or of one where the higher cell is a cliff), otherwise the extra steps a climb costs.
 */
export const landStep = (land: LandDef, a: number, b: number): number => {
  const la = land.level[a] as number;
  const lb = land.level[b] as number;
  if (la === lb) return 0;
  const d = lb - la;
  if (d > 1 || d < -1) return -1;
  if (land.cliff[d > 0 ? b : a] === 1) return -1;
  return d > 0 ? LAND.climbSteps : 0;
};

/** Whether anyone can stand on terraced land of this kind. */
export const landGround = (kind: number): boolean => kind !== GROUND.rock && kind !== GROUND.water && kind !== GROUND.vein;

/** Cells where players may build. */
export const isBuildableTerrain = (t: number): boolean => t === T_INTERIOR;
const groundTerrain = (t: number): boolean =>
  t === T_INTERIOR || t === T_PLAZA || t === T_PATH || t === T_SPAWN || t === T_GATE || t === T_KEEP || t === T_WOOD || t === T_FORD;

interface Cache {
  terrain: number[];
  rev: number;
  /** cell id → index into structures (blocking or trap), -1 none. */
  structAt: Int32Array;
  trapAt: Int32Array;
  /** Ground flow field to the keep (to the caravan, on a saga escort): cost-to-go per cell (−1 unreachable). */
  flow: Int32Array;
  /** The cell the flow field leads to. */
  goal: number;
  /** Cells blocked by enemy obstacles (bone walls). */
  bone: Uint8Array;
  /** Closed dungeon doors (block heroes and nightmares). */
  door: Uint8Array;
  /** Ring cells whose wall run lies broken: open ground. */
  breach: Uint8Array;
  /** The bastion's surroundings are walkable (rough) ground: nightmares come from every side. */
  wild: boolean;
  keepCell: number;
  /** Flow fields to wall spots outside the bastion (cell → cost-to-go, −1 unreachable), built on demand. */
  spots: Map<number, Int32Array>;
  /** The mammoth's straight way to the goal, through what stands (cost-to-go, −1 unreachable), built on demand. */
  trample: Int32Array | null;
  /** Terraced land: the ways in for nightmares that see walls cheaper or dearer than most, built on demand. */
  byClass: Map<WallClass, Int32Array>;
}

export const cache = (s: BattleState): Cache => {
  let c = s._cache as Cache | undefined;
  if (!c) {
    const terrain = buildTerrain(s.map);
    const n = terrain.length;
    c = {
      terrain,
      rev: -1,
      structAt: new Int32Array(n),
      trapAt: new Int32Array(n),
      flow: new Int32Array(n),
      bone: new Uint8Array(n),
      door: new Uint8Array(n),
      breach: new Uint8Array(n),
      wild: s.map.kind === 'bastion',
      keepCell: cellId(s.map.radius, 0, 0),
      goal: -1,
      spots: new Map(),
      trample: null,
      byClass: new Map(),
    };
    s._cache = c;
  }
  const goal = flowGoal(s, c);
  if (c.rev !== s.gridRev || c.goal !== goal) {
    // The health the ways are weighed by is taken once per grid change, into the battle itself: a cache built
    // again from scratch (a copy of the battle) weighs them the same as the one built along the way.
    if (s.pathRev !== s.gridRev) {
      for (const st of s.structures) st.pathHp = st.hp;
      s.pathRev = s.gridRev;
    }
    c.goal = goal;
    rebuild(s, c);
  }
  return c;
};

/** Where the ground flow leads: the keep, or the caravan on a saga escort (there is no node to go for). */
const flowGoal = (s: BattleState, c: Cache): number => {
  const e = s.escort;
  if (!e) return c.keepCell;
  const cart = s.bots?.find((b) => b.id === e.cart);
  if (!cart || cart.hp <= 0) return c.keepCell;
  const id = worldToCell(s.map.radius, cart.x, cart.y);
  return id >= 0 && groundTerrain(c.terrain[id] as number) ? id : c.keepCell;
};

/** Mark the grid dirty: structure built/destroyed. */
export const touchGrid = (s: BattleState): void => {
  s.gridRev++;
};

const STEP = 10;

const rebuild = (s: BattleState, c: Cache): void => {
  c.structAt.fill(-1);
  c.trapAt.fill(-1);
  const R = s.map.radius;
  for (let i = 0; i < s.structures.length; i++) {
    const st = s.structures[i] as Structure;
    if (st.destroyed) continue;
    const id = cellId(R, st.q, st.r);
    if (id < 0) continue;
    if (STRUCTURES[st.type].blocks) c.structAt[id] = i;
    else c.trapAt[id] = i;
    if (st.foot)
      for (const h of st.foot) {
        const f = cellId(R, h.q, h.r);
        if (f >= 0) c.structAt[f] = i;
      }
  }
  c.bone.fill(0);
  c.door.fill(0);
  c.breach.fill(0);
  for (const st of s.structures) {
    if (st.type !== 'rampart' || !st.destroyed) continue;
    const id = cellId(R, st.q, st.r);
    if (id >= 0) c.breach[id] = 1;
  }
  const doors = s.map.dungeon?.doors ?? [];
  doors.forEach((d, i) => {
    if (!doorOpen(s, i)) {
      const id = cellId(R, d.q, d.r);
      if (id >= 0) c.door[id] = 1;
    }
  });
  for (const e of s.enemies) {
    if (e.kind !== 'boneWall' || e.hp <= 0) continue;
    const h = worldToHex(e.x, e.y);
    const id = cellId(R, h.q, h.r);
    if (id >= 0) c.bone[id] = 1;
  }
  computeFlow(s, c);
  c.spots.clear();
  c.trample = null;
  c.byClass.clear();
  c.rev = s.gridRev;
};

/**
 * The way to a wall spot just outside the bastion, keeping outside the wall and round whatever stands in the
 * way: cost-to-go per cell (−1 unreachable). For a live general's strikes and pulses at the far side.
 */
export const spotFlow = (s: BattleState, spot: number): Int32Array => {
  const c = cache(s);
  let f = c.spots.get(spot);
  if (f) return f;
  const R = s.map.radius;
  const nb = geo(R).nb;
  f = new Int32Array(c.terrain.length).fill(-1);
  const out = (id: number): number => {
    if (id < 0 || c.structAt[id] as number >= 0) return -1;
    const h = cellHex(R, id);
    if (s.map.land ? isInside(s.map, h.q, h.r) : hexLen(h.q, h.r) <= s.map.bastion) return -1;
    return enterCost(s, c, id);
  };
  const land = s.map.land;
  const heap = new Heap();
  f[spot] = 0;
  heap.push(0, spot);
  while (heap.size > 0) {
    const d = heap.topKey();
    const id = heap.popId();
    if (d !== f[id]) continue;
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      const e = out(n);
      if (e < 0) continue;
      const x = land ? landStep(land, n, id) : 0;
      if (x < 0) continue;
      const nd = d + e + x * STEP;
      const cur = f[n] as number;
      if (cur < 0 || nd < cur) {
        f[n] = nd;
        heap.push(nd, n);
      }
    }
  }
  c.spots.set(spot, f);
  return f;
};

/**
 * Ground under a cell: the usual terrain, plus (around the bastion) the rough land beyond the paths
 * and the ring wherever its wall stands as a run to break or lies broken.
 */
const groundAt = (c: Cache, id: number): boolean => {
  const t = c.terrain[id] as number;
  if (groundTerrain(t)) return true;
  if (!c.wild) return false;
  if (t === T_VOID) return true;
  return t === T_RAMPART && (c.breach[id] === 1 || (c.structAt[id] as number) >= 0);
};

/** Rough land beyond the paths costs this many steps per cell (paths are quicker). */
const WILD_STEPS = 2;

/** Cost to step into a cell for ground nightmares; −1 = impassable. `wallBp` weighs walls for a wall class (terraced land). */
const enterCost = (s: BattleState, c: Cache, id: number, wallBp = 10000): number => {
  const t = c.terrain[id] as number;
  if (!groundAt(c, id)) return -1;
  if (t === T_VOID) return STEP * WILD_STEPS;
  if (t === T_WOOD) return STEP * LAND.woodSteps;
  if (t === T_FORD) return STEP * LAND.fordSteps;
  if (t === T_KEEP) return STEP;
  if (c.bone[id] === 1) return -1;
  const si = c.structAt[id] as number;
  if (si >= 0) {
    const st = s.structures[si] as Structure;
    // A standing wall costs twice what a gate does: the path leads to its gate, not to the wall beside it.
    const wall = st.type === 'rampart' || st.type === 'wall';
    return STEP + 10 + idiv(idiv(st.pathHp ?? st.hp, wall ? 4 : 8) * wallBp, 10000);
  }
  return STEP;
};

/** Ids stay below this (cell ids of a (2R+1)² box), so (cost, id) packs into one exact integer. */
const ID_SPAN = 65536;

/**
 * Binary min-heap keyed by (cost, id) — deterministic tie-break. Each entry is packed as
 * cost·ID_SPAN + id (exact below 2^53), so one comparison orders by cost, then by id. Costs can go
 * negative (a structure beaten below 0 hp still stands until it falls): unpack with floor division.
 */
class Heap {
  private a: number[] = [];
  get size(): number {
    return this.a.length;
  }
  push(cost: number, id: number): void {
    if (id < 0 || id >= ID_SPAN) throw new Error(`Heap: id ${id} out of range`);
    const a = this.a;
    const x = cost * ID_SPAN + id;
    let i = a.length;
    a.push(x);
    while (i > 0) {
      const p = (i - 1) >> 1;
      const ap = a[p] as number;
      if (x >= ap) break;
      a[i] = ap;
      i = p;
    }
    a[i] = x;
  }
  /** Key of the smallest entry (call before popId). */
  topKey(): number {
    return fdiv(this.a[0] as number, ID_SPAN);
  }
  /** Pops the smallest entry and returns its id. */
  popId(): number {
    const a = this.a;
    const top = a[0] as number;
    const last = a.pop() as number;
    const n = a.length;
    if (n > 0) {
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        if (l >= n) break;
        const r = l + 1;
        let m = l;
        let am = a[l] as number;
        if (r < n && (a[r] as number) < am) {
          m = r;
          am = a[r] as number;
        }
        if (am >= last) break;
        a[i] = am;
        i = m;
      }
      a[i] = last;
    }
    return top - fdiv(top, ID_SPAN) * ID_SPAN;
  }
}

const computeFlow = (s: BattleState, c: Cache): void => fill(s, c, c.flow, (id) => enterCost(s, c, id));

/** Cost-to-go to the goal over `field`, stepping into a cell for `cost` (−1 impassable). */
const fill = (s: BattleState, c: Cache, field: Int32Array, cost: (id: number) => number): void => {
  const nb = geo(s.map.radius).nb;
  const land = s.map.land;
  field.fill(-1);
  const heap = new Heap();
  field[c.goal] = 0;
  heap.push(0, c.goal);
  while (heap.size > 0) {
    const d = heap.topKey();
    const id = heap.popId();
    if (d !== field[id]) continue;
    const enter = cost(id);
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0) continue;
      if (cost(n) < 0) continue;
      // Terraced land: from n up or down into id, unless a cliff stands between.
      const x = land ? landStep(land, n, id) : 0;
      if (x < 0) continue;
      const nd = d + enter + x * STEP;
      const cur = field[n] as number;
      if (cur < 0 || nd < cur) {
        field[n] = nd;
        heap.push(nd, n);
      }
    }
  }
};

/**
 * The mammoth's way: every cell it can stand on costs the same (rough land too), and one with a building
 * on it a little less, so of the straight ways in it takes the one through the most buildings.
 */
const trampleCost = (s: BattleState, c: Cache, id: number): number => {
  if (enterCost(s, c, id) < 0) return -1;
  return (c.structAt[id] as number) >= 0 ? STEP - 2 : STEP;
};

/** The mammoth's straight way to the goal (cost-to-go per cell, −1 unreachable). */
export const trampleFlow = (s: BattleState): Int32Array => {
  const c = cache(s);
  if (!c.trample) {
    c.trample = new Int32Array(c.terrain.length);
    fill(s, c, c.trample, (id) => trampleCost(s, c, id));
  }
  return c.trample;
};

/**
 * Terraced land: the way in for nightmares of a wall class (cost-to-go per cell, −1 unreachable). The usual
 * flow serves the normal class; breakers and skirters get their own, built when first asked.
 */
export const classFlow = (s: BattleState, cls: WallClass): Int32Array => {
  const c = cache(s);
  if (cls === 'normal' || !s.map.land) return c.flow;
  let f = c.byClass.get(cls);
  if (!f) {
    f = new Int32Array(c.terrain.length);
    const bp = LAND.wallClassBp[cls];
    fill(s, c, f, (id) => enterCost(s, c, id, bp));
    c.byClass.set(cls, f);
  }
  return f;
};

/** Next cell for a ground nightmare standing on `id`: the neighbor with the lowest cost-to-go (on the mammoth's way, if it tramples; on its wall class's way on terraced land). */
export const flowNext = (s: BattleState, id: number, tramples = false, cls: WallClass = 'normal'): number => {
  const c = cache(s);
  const field = tramples ? trampleFlow(s) : classFlow(s, cls);
  const land = s.map.land;
  const wallBp = land ? LAND.wallClassBp[cls] : 10000;
  const here = field[id] as number;
  if (here === 0) return id;
  if (id < 0) return -1;
  const nb = geo(s.map.radius).nb;
  let best = -1;
  let bestD = 0x7fffffff;
  for (let k = id * 6, end = k + 6; k < end; k++) {
    const n = nb[k] as number;
    if (n < 0) continue;
    const d = field[n] as number;
    if (d < 0) continue;
    const e = tramples ? trampleCost(s, c, n) : enterCost(s, c, n, wallBp);
    if (e < 0) continue;
    const x = land ? landStep(land, id, n) : 0;
    if (x < 0) continue;
    const total = d + e + x * STEP;
    if (here >= 0 && total > here) continue;
    if (total < bestD || (total === bestD && n < best)) {
      bestD = total;
      best = n;
    }
  }
  return best;
};

export const terrainAt = (s: BattleState, q: number, r: number): number => {
  const id = cellId(s.map.radius, q, r);
  return id < 0 ? T_VOID : (cache(s).terrain[id] as number);
};

export const structureAtCell = (s: BattleState, id: number): Structure | null => {
  if (id < 0) return null;
  const c = cache(s);
  const i = c.structAt[id] as number;
  if (i >= 0) return s.structures[i] as Structure;
  const j = c.trapAt[id] as number;
  return j >= 0 ? (s.structures[j] as Structure) : null;
};

export const blockingAtCell = (s: BattleState, id: number): Structure | null => {
  if (id < 0) return null;
  const i = cache(s).structAt[id] as number;
  return i >= 0 ? (s.structures[i] as Structure) : null;
};

export const trapAtCell = (s: BattleState, id: number): Structure | null => {
  if (id < 0) return null;
  const i = cache(s).trapAt[id] as number;
  return i >= 0 ? (s.structures[i] as Structure) : null;
};

export const cellAtWorld = (s: BattleState, x: number, y: number): number => worldToCell(s.map.radius, x, y);

/**
 * Door `i` leads into room `i + 1`: open once the room before it is cleared, shut while the party
 * fights in the room behind it, and down for good once that room is cleared too (the party has moved
 * on; clearing a room gets every fallen hero back up, so nobody is left behind it).
 */
export const doorOpen = (s: BattleState, i: number): boolean => {
  const d = s.dungeon;
  if (!d) return true;
  // An expedition opens and shuts its own doors (battle/expedition.ts).
  if (d.exp) return d.exp.open[i] ?? true;
  // The Furrows: gates by their levers, breathing walls by the titan's breath (battle/maze.ts).
  if (d.maze) return mazeDoorOpen(d.maze, i);
  // El pozo 2: the Drinker burst, every door stands open for the way back up (the water comes after).
  if (s.drink?.burst) return true;
  // El Párpado 1: the neck's folds shut on the in-breath, whatever else (battle/neck.ts).
  if (!neckOpen(s)) return false;
  // The Warden's door stays sealed until the runes are stepped on in order (crypt in embers).
  if (d.seal && !d.seal.solved && i + 2 === (s.map.dungeon?.rooms.length ?? 0)) return false;
  // The deep crypt: no door into a lower floor's entrance (the stairs lead there), and a puzzle holds its way out until solved.
  if (s.map.dungeon?.rooms[i + 1]?.kind === 'entry') return true;
  if (d.puzzles?.some((p) => p.room === i && !p.solved)) return false;
  // Shut behind the party once the room ahead wakes, as soon as everyone still in the corridor has walked in.
  return (d.cleared[i] ?? false) && !(d.cleared[i + 1] ?? false) && (d.room !== i + 1 || (d.joining?.length ?? 0) > 0);
};

/** Heroes walk on ground terrain not blocked by structures (gates are friendly) or closed doors. */
export const heroWalkable = (s: BattleState, id: number): boolean => (id < 0 ? false : walkableIn(s, cache(s), id));
const walkableIn = (s: BattleState, c: Cache, id: number): boolean => {
  const t = c.terrain[id] as number;
  if (!groundAt(c, id) || t === T_KEEP) return false;
  if (c.door[id] === 1) return false;
  const si = c.structAt[id] as number;
  if (si < 0) return true;
  const st = s.structures[si] as Structure;
  return st.type === 'gate';
};

/**
 * Heroes may cross their own towers, traps and walls (they climb through the garrison and over the
 * parapet) but not the bastion's ring, nor a wall drawn on terraced land. They can pass a cell like that, never stop on it: see `heroWalkable`.
 */
export const heroPassable = (s: BattleState, id: number): boolean => (id < 0 ? false : passableIn(s, cache(s), id));
const passableIn = (s: BattleState, c: Cache, id: number): boolean => {
  const t = c.terrain[id] as number;
  if (!groundAt(c, id) || t === T_KEEP) return false;
  if (c.door[id] === 1) return false;
  const si = c.structAt[id] as number;
  if (si < 0) return true;
  // The ring holds heroes (until it lies broken): out only through a gate or a breach. On terraced land the
  // walls the group draws hold them the same way (docs/CONSTRUCCION.md).
  const type = (s.structures[si] as Structure).type;
  return type !== 'rampart' && !(type === 'wall' && s.map.land);
};

/** How far around a cell a hero can't stand on it looks for one it can (rings of cells). */
const STAND_REACH = 3;
/** Free cells tried, nearest first, before giving up on standing next to it. */
const STAND_TRIES = 8;

/**
 * A hero's walk toward `to`: a `findPath` that always ends on a cell it can stand on. Sent onto a
 * tower or a wall, it stops on the reachable free cell nearest it (by rings, then lower id), not
 * just on the last free cell the path crossed (in a field of towers that can be where it started).
 */
export const heroRoute = (s: BattleState, from: number, to: number): number[] => {
  if (to < 0 || heroWalkable(s, to)) return findPath(s, from, to);
  const { nb } = geo(s.map.radius);
  const seen = new Set<number>([to]);
  let ring = [to];
  let tries = 0;
  for (let d = 1; d <= STAND_REACH && tries < STAND_TRIES; d++) {
    const next: number[] = [];
    for (const id of ring) {
      for (let k = id * 6, end = k + 6; k < end; k++) {
        const n = nb[k] as number;
        if (n < 0 || seen.has(n)) continue;
        seen.add(n);
        next.push(n);
      }
    }
    next.sort((a, b) => a - b);
    // Already standing beside it: stay.
    if (next.includes(from) && heroWalkable(s, from)) return [];
    for (const n of next) {
      if (!heroWalkable(s, n) || tries >= STAND_TRIES) continue;
      tries++;
      const p = findPath(s, from, n);
      if (p[p.length - 1] === n) return p;
    }
    ring = next;
  }
  // Nothing free near it: as far as the walk goes, back to the last cell it can stand on.
  const p = findPath(s, from, to);
  while (p.length > 0 && !heroWalkable(s, p[p.length - 1] as number)) p.pop();
  return p;
};

/**
 * A* over hero-passable cells with deterministic tie-break (lower f, then lower h, then lower id).
 * Returns cell ids from the first step to the goal (or to the closest reachable cell to it).
 */
export const findPath = (s: BattleState, from: number, to: number): number[] => {
  const R = s.map.radius;
  if (from < 0 || to < 0) return [];
  if (from === to) return [];
  const { q: gq, r: gr, nb } = geo(R);
  const c = cache(s);
  const land = s.map.land;
  const goalQ = gq[to] as number;
  const goalR = gr[to] as number;
  const h = (id: number): number => hexLen((gq[id] as number) - goalQ, (gr[id] as number) - goalR) * STEP;
  // g, parent and closed live in reusable typed arrays; a cell counts only if stamped by this search.
  const sc = scratch(gridW(R) * gridW(R));
  const stamp = ++sc.gen;
  const { g, parent, seen, closed } = sc;
  // Heap key packs f and h so ties resolve by lower h, then the heap's own id tie-break.
  const heap = new Heap();
  const key = (f: number, hh: number): number => f * 4096 + hh;
  seen[from] = stamp;
  g[from] = 0;
  parent[from] = -1;
  heap.push(key(h(from), h(from)), from);
  let best = from;
  let bestH = h(from);
  let expanded = 0;
  while (heap.size > 0 && expanded < 4000) {
    const id = heap.popId();
    if (closed[id] === stamp) continue;
    closed[id] = stamp;
    expanded++;
    const hid = h(id);
    if (walkableIn(s, c, id) && (hid < bestH || (hid === bestH && id < best))) {
      best = id;
      bestH = hid;
    }
    if (id === to) break;
    const gid = g[id] as number;
    for (let k = id * 6, end = k + 6; k < end; k++) {
      const n = nb[k] as number;
      if (n < 0 || closed[n] === stamp) continue;
      if (!passableIn(s, c, n)) continue;
      if (land && landStep(land, id, n) < 0) continue;
      const ng = gid + STEP;
      if (seen[n] !== stamp || ng < (g[n] as number)) {
        seen[n] = stamp;
        g[n] = ng;
        parent[n] = id;
        const hn = h(n);
        heap.push(key(ng + hn, hn), n);
      }
    }
  }
  const end = closed[to] === stamp ? to : best;
  const out: number[] = [];
  let cur = end;
  while (cur !== from) {
    out.push(cur);
    const p = seen[cur] === stamp ? (parent[cur] as number) : -1;
    if (p < 0) break;
    cur = p;
  }
  out.reverse();
  return out;
};

interface Scratch {
  gen: number;
  g: Int32Array;
  parent: Int32Array;
  seen: Uint32Array;
  closed: Uint32Array;
}
/** Search buffers by grid size. Not state: every search stamps what it writes, so leftovers never count. */
const SCRATCH = new Map<number, Scratch>();
const scratch = (n: number): Scratch => {
  let sc = SCRATCH.get(n);
  if (!sc || sc.gen >= 0xfffffff0) {
    sc = { gen: 0, g: new Int32Array(n), parent: new Int32Array(n), seen: new Uint32Array(n), closed: new Uint32Array(n) };
    SCRATCH.set(n, sc);
  }
  return sc;
};

export const isGround = groundTerrain;
export { T_RAMPART };
